import { useState, ReactElement } from 'react'
import { LogOut, ChevronUp, Sun, Moon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useTheme } from '../../contexts/theme-context'
import { LanguageSwitcher } from '../../i18n/components/LanguageSwitcher'

export interface UserMenuProps {
  user?:
    | {
        name: string
        email?: string | undefined
        avatarUrl?: string | undefined
      }
    | undefined
  workspace?:
    | {
        name: string
      }
    | undefined
  onLogout?: (() => void) | undefined
  /** Whether the user is authenticated (for language persistence) */
  isAuthenticated?: boolean | undefined
}

function getInitials(name: string): string {
  return name
    .split(' ')
    .map((part) => part[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

type UserInfo = NonNullable<UserMenuProps['user']>

function UserAvatar({ user }: { user: UserInfo }): ReactElement {
  if (user.avatarUrl) {
    return (
      <img src={user.avatarUrl} alt={user.name} className="h-9 w-9 rounded-full object-cover" />
    )
  }
  return (
    <div
      className="flex h-9 w-9 items-center justify-center rounded-full"
      style={{ background: 'var(--accent-dim)' }}
    >
      <span className="text-sm font-medium" style={{ color: 'var(--accent)' }}>
        {getInitials(user.name)}
      </span>
    </div>
  )
}

interface UserMenuToggleProps {
  user: UserInfo
  workspace?: { name: string } | undefined
  expanded: boolean
  onToggle: () => void
}

function UserMenuToggle({
  user,
  workspace,
  expanded,
  onToggle,
}: UserMenuToggleProps): ReactElement {
  return (
    <button
      onClick={onToggle}
      className="flex w-full items-center gap-3 rounded-lg p-2 transition-colors"
      style={{ color: 'var(--text)' }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = 'var(--hover)'
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = 'transparent'
      }}
    >
      <UserAvatar user={user} />
      <div className="min-w-0 flex-1 text-left">
        <p className="truncate text-sm font-medium" style={{ color: 'var(--text)' }}>
          {user.name}
        </p>
        {workspace && (
          <p className="truncate text-xs" style={{ color: 'var(--text-muted)' }}>
            {workspace.name}
          </p>
        )}
      </div>
      <ChevronUp
        className={`h-4 w-4 transition-transform ${expanded ? 'rotate-180' : ''}`}
        style={{ color: 'var(--text-muted)' }}
      />
    </button>
  )
}

interface ExpandedMenuProps {
  user: UserInfo
  isAuthenticated: boolean
  theme: string
  onToggleTheme: () => void
  onLogout: () => void
  signOutLabel: string
}

const hoverIn = (e: React.MouseEvent<HTMLButtonElement>): void => {
  e.currentTarget.style.background = 'var(--hover)'
}
const hoverOut = (e: React.MouseEvent<HTMLButtonElement>): void => {
  e.currentTarget.style.background = 'transparent'
}

function ExpandedMenu({
  user,
  isAuthenticated,
  theme,
  onToggleTheme,
  onLogout,
  signOutLabel,
}: ExpandedMenuProps): ReactElement {
  return (
    <div className="mt-1 rounded-lg py-1" style={{ background: 'var(--highlight)' }}>
      {user.email && (
        <div className="truncate px-3 py-2 text-xs" style={{ color: 'var(--text-muted)' }}>
          {user.email}
        </div>
      )}
      <LanguageSwitcher variant="menu" isAuthenticated={isAuthenticated} />
      <button
        onClick={onToggleTheme}
        className="flex w-full items-center gap-2 px-3 py-2 text-sm transition-colors"
        style={{ color: 'var(--text-dim)' }}
        onMouseEnter={hoverIn}
        onMouseLeave={hoverOut}
      >
        {theme === 'light' ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
        <span>{theme === 'light' ? 'Dark mode' : 'Light mode'}</span>
      </button>
      <button
        onClick={onLogout}
        className="flex w-full items-center gap-2 px-3 py-2 text-sm transition-colors"
        style={{ color: 'var(--text-dim)' }}
        onMouseEnter={hoverIn}
        onMouseLeave={hoverOut}
      >
        <LogOut className="h-4 w-4" />
        <span>{signOutLabel}</span>
      </button>
    </div>
  )
}

export function UserMenu({
  user,
  workspace,
  onLogout,
  isAuthenticated = false,
}: UserMenuProps): ReactElement | null {
  const [expanded, setExpanded] = useState(false)
  const { t } = useTranslation('auth')
  const { theme, toggleTheme } = useTheme()

  if (!user) return null

  const handleLogout = (): void => {
    setExpanded(false)
    onLogout?.()
  }

  return (
    <div className="p-2">
      <UserMenuToggle
        user={user}
        workspace={workspace}
        expanded={expanded}
        onToggle={() => {
          setExpanded(!expanded)
        }}
      />
      {expanded && (
        <ExpandedMenu
          user={user}
          isAuthenticated={isAuthenticated}
          theme={theme}
          onToggleTheme={toggleTheme}
          onLogout={handleLogout}
          signOutLabel={t('actions.signOut')}
        />
      )}
    </div>
  )
}
